import { RouteObject, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '@/components/layouts/MainLayout';
import AuthGuard from '@/components/guards/AuthGuard';
import GuestGuard from '@/components/guards/GuestGuard';
import LoadingScreen from '@/components/common/LoadingScreen';

// Lazy load components
const UnifiedLoginPage = lazy(() => import('@/pages/UnifiedLogin'));
const RegisterCustomerPage = lazy(() => import('@/pages/RegisterCustomer'));
const RegisterSupplierPage = lazy(() => import('@/pages/RegisterSupplier'));
const RegisterThankYouPage = lazy(() => import('@/pages/RegisterThankYou'));
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmail'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPassword'));

const DashboardPage = lazy(() => import('@/pages/Dashboard'));
const CustomerDashboardPage = lazy(() => import('@/pages/CustomerDashboard'));
const SupplierDashboardPage = lazy(() => import('@/pages/SupplierDashboard'));

const ProfilePage = lazy(() => import('@/pages/Profile'));
const ProfilePasswordPage = lazy(() => import('@/pages/ProfilePassword'));
const SettingsPage = lazy(() => import('@/pages/Settings'));
const SettingsInfoPage = lazy(() => import('@/pages/SettingsInfo'));

const TicketsCustomerPage = lazy(() => import('@/pages/TicketsCustomer'));
const TicketsCustomerDetailPage = lazy(() => import('@/pages/TicketsCustomerDetail'));
const TicketsSupplierPage = lazy(() => import('@/pages/TicketsSupplier'));
const TicketsSupplierDetailPage = lazy(() => import('@/pages/TicketsSupplierDetail'));
const TicketsAdminPage = lazy(() => import('@/pages/TicketsAdmin'));
const TicketsAdminDetailPage = lazy(() => import('@/pages/TicketsAdminDetail'));
const TicketsCreatePage = lazy(() => import('@/pages/TicketsCreate'));

const ProductsCustomerPage = lazy(() => import('@/pages/ProductsCustomer'));

const ProductsSupplierPage = lazy(() => import('@/pages/ProductsSupplier'));
const ProductsSupplierCreatePage = lazy(() => import('@/pages/ProductsSupplierCreate'));
const ProductsSupplierEditPage = lazy(() => import('@/pages/ProductsSupplierEdit'));
const ProductsAdminEditPage = lazy(() => import('@/pages/ProductsAdminEdit'));
const ProductsLibraryPage = lazy(() => import('@/pages/ProductsLibrary'));
const ProductsApprovalPage = lazy(() => import('@/pages/ProductsApproval'));
const ProductDetailPage = lazy(() => import('@/pages/ProductDetail'));
const ProductCreatePage = lazy(() => import('@/pages/ProductCreate'));

const AdminListPage = lazy(() => import('@/pages/AdminList'));
const SystemUsersPage = lazy(() => import('@/pages/SystemUsers'));
const SuppliersListPage = lazy(() => import('@/pages/SuppliersList'));
const CustomersListPage = lazy(() => import('@/pages/CustomersList'));
const SystemRolesPage = lazy(() => import('@/pages/SystemRoles'));
const SystemPermissionsPage = lazy(() => import('@/pages/SystemPermissions'));
const SystemAttachmentsPage = lazy(() => import('@/pages/SystemAttachments'));

const ApprovalListPage = lazy(() => import('@/pages/ApprovalList'));
const OperationLogsPage = lazy(() => import('@/pages/OperationLogs'));

const NotificationsPage = lazy(() => import('@/pages/Notifications'));
const NotFoundPage = lazy(() => import('@/pages/NotFound'));

const Lazy = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
);

const routes: RouteObject[] = [
  // Guest routes (login, register)
  {
    path: '/',
    element: <GuestGuard />,
    children: [
      {
        path: 'login',
        element: <Lazy><UnifiedLoginPage /></Lazy>,
      },
      {
        path: 'register/customer',
        element: <Lazy><RegisterCustomerPage /></Lazy>,
      },
      {
        path: 'register/supplier',
        element: <Lazy><RegisterSupplierPage /></Lazy>,
      },
      {
        path: 'register/thank-you',
        element: <Lazy><RegisterThankYouPage /></Lazy>,
      },
      {
        path: 'verify-email',
        element: <Lazy><VerifyEmailPage /></Lazy>,
      },
      {
        path: 'forgot-password',
        element: <Lazy><ForgotPasswordPage /></Lazy>,
      },
      {
        path: 'reset-password',
        element: <Lazy><ResetPasswordPage /></Lazy>,
      },
    ],
  },
  // Protected routes
  {
    path: '/',
    element: <AuthGuard />,
    children: [
      // Standalone ticket detail pages (no sidebar)
      { path: 'tickets/customer/:id', element: <Lazy><TicketsCustomerDetailPage /></Lazy> },
      { path: 'tickets/supplier/:id', element: <Lazy><TicketsSupplierDetailPage /></Lazy> },
      { path: 'tickets/chat', element: <Lazy><TicketsAdminDetailPage /></Lazy> },
      { path: 'tickets/chat/:type/:id', element: <Lazy><TicketsAdminDetailPage /></Lazy> },
      {
        element: <MainLayout />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          {
            path: 'dashboard',
            children: [
              { index: true, element: <Lazy><DashboardPage /></Lazy> },
              { path: 'customer', element: <Lazy><CustomerDashboardPage /></Lazy> },
              { path: 'supplier', element: <Lazy><SupplierDashboardPage /></Lazy> },
            ],
          },
          {
            path: 'profile',
            children: [
              { index: true, element: <Lazy><ProfilePage /></Lazy> },
              { path: 'password', element: <Lazy><ProfilePasswordPage /></Lazy> },
              { path: 'customer', element: <Lazy><ProfilePage /></Lazy> },
              { path: 'customer/password', element: <Lazy><ProfilePasswordPage /></Lazy> },
              { path: 'supplier', element: <Lazy><ProfilePage /></Lazy> },
              { path: 'supplier/password', element: <Lazy><ProfilePasswordPage /></Lazy> },
            ],
          },
          {
            path: 'settings',
            children: [
              { index: true, element: <Lazy><SettingsPage /></Lazy> },
              { path: 'info', element: <Lazy><SettingsInfoPage /></Lazy> },
            ],
          },
          {
            path: 'tickets',
            children: [
              { path: 'customer', element: <Lazy><TicketsCustomerPage /></Lazy> },
              { path: 'supplier', element: <Lazy><TicketsSupplierPage /></Lazy> },
              { path: 'admin', element: <Lazy><TicketsAdminPage /></Lazy> },
              { path: 'create', element: <Lazy><TicketsCreatePage /></Lazy> },
            ],
          },
          {
            path: 'products',
            children: [
              { path: 'customer', element: <Lazy><ProductsCustomerPage /></Lazy> },
              { path: 'supplier', element: <Lazy><ProductsSupplierPage /></Lazy> },
              { path: 'supplier/create', element: <Lazy><ProductsSupplierCreatePage /></Lazy> },
              { path: 'supplier/edit/:id', element: <Lazy><ProductsSupplierEditPage /></Lazy> },
              { path: 'library', element: <Lazy><ProductsLibraryPage /></Lazy> },
              { path: 'library/customer', element: <Lazy><ProductsLibraryPage /></Lazy> },
              { path: 'approval', element: <Lazy><ProductsApprovalPage /></Lazy> },
              { path: 'create', element: <Lazy><ProductCreatePage /></Lazy> },
              { path: 'edit/:id', element: <Lazy><ProductsAdminEditPage /></Lazy> },
              { path: ':id', element: <Lazy><ProductDetailPage /></Lazy> },
            ],
          },
          {
            path: 'approval',
            children: [
              { path: 'list', element: <Lazy><ApprovalListPage /></Lazy> },
            ],
          },
          {
            path: 'admin',
            children: [
              { path: 'list', element: <Lazy><AdminListPage /></Lazy> },
            ],
          },
          {
            path: 'system',
            children: [
              { path: 'users', element: <Lazy><SystemUsersPage /></Lazy> },
              { path: 'suppliers', element: <Lazy><SuppliersListPage /></Lazy> },
              { path: 'customers', element: <Lazy><CustomersListPage /></Lazy> },
              { path: 'roles', element: <Lazy><SystemRolesPage /></Lazy> },
              { path: 'permissions', element: <Lazy><SystemPermissionsPage /></Lazy> },
              { path: 'attachments', element: <Lazy><SystemAttachmentsPage /></Lazy> },
              { path: 'operation-logs', element: <Lazy><OperationLogsPage /></Lazy> },
            ],
          },
          { path: 'notifications', element: <Lazy><NotificationsPage /></Lazy> },
        ],
      },
    ],
  },
  // 404 page
  {
    path: '*',
    element: <Lazy><NotFoundPage /></Lazy>,
  },
];

export default routes;
